import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface ARTCompanyDataScreenProps {
	onNext: (data: { razonSocial: string; cuit: string }) => void
	onBack: () => void
	data?: { razonSocial?: string; cuit?: string }
}

export function ARTCompanyDataScreen({
	onNext,
	onBack,
	data,
}: ARTCompanyDataScreenProps) {
	const [formData, setFormData] = useState({
		razonSocial: data?.razonSocial || '',
		cuit: data?.cuit || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const validateCUIT = (cuit: string) => {
		// Remove hyphens for validation
		const cleaned = cuit.replace(/-/g, '')
		return cleaned.length === 11 && /^\d+$/.test(cleaned)
	}

	const formatCUIT = (value: string) => {
		// Remove non-digits
		const cleaned = value.replace(/\D/g, '')

		// Format as XX-XXXXXXXX-X
		if (cleaned.length <= 2) {
			return cleaned
		} else if (cleaned.length <= 10) {
			return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`
		} else {
			return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10, 11)}`
		}
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.razonSocial.trim()) {
			newErrors.razonSocial = 'Ingresa la razón social'
		}
		if (!formData.cuit.trim()) {
			newErrors.cuit = 'Ingresa el CUIT'
		} else if (!validateCUIT(formData.cuit)) {
			newErrors.cuit = 'CUIT inválido (formato: XX-XXXXXXXX-X)'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid = formData.razonSocial.trim() && formData.cuit.trim()

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-8">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Completa los datos de
						<br />
						tu empresa
					</h2>

					{/* Form inputs */}
					<div className="flex w-full flex-col gap-6">
						<InputText
							label="Razón social"
							labelPosition="internal"
							size="l"
							placeholder="Razón social"
							value={formData.razonSocial}
							onChange={(e) => {
								setFormData({ ...formData, razonSocial: e.target.value })
								setErrors({ ...errors, razonSocial: '' })
							}}
							errorText={errors.razonSocial}
							required
						/>
						<InputText
							label="CUIT"
							labelPosition="internal"
							size="l"
							placeholder="00-00000000-0"
							value={formData.cuit}
							onChange={(e) => {
								const formatted = formatCUIT(e.target.value)
								setFormData({ ...formData, cuit: formatted })
								setErrors({ ...errors, cuit: '' })
							}}
							errorText={errors.cuit}
							maxLength={13}
							required
						/>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
